import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { pubblicoFetch, ErrorePubblico } from './apiPubblica';
import { Contenitore, Intestazione, Contatti, PaginaNonDisponibile, SceltaSlot, SedePubblica, useTitoloPagina } from './Componenti';
import { AspettoPagina } from './aspetto';
import { formatQuandoRoma } from '../utils/dateRoma';
import { maiuscolaIniziale } from './testo';

interface DatiGestione {
  stato: 'APERTA' | 'CHIUSA';
  tokenPagina: string | null;
  sede: SedePubblica | null;
  cantiere?: { nome: string };
  // Qui conta solo il tema: il modello della pagina non si usa nella gestione dell'appuntamento.
  aspetto?: AspettoPagina;
  appuntamento: { inizio: string; fine: string; stato: 'PRENOTATO' | 'SVOLTO' | 'ANNULLATO' | 'NON_PRESENTATO'; numeroIncontro: number };
}
interface EsitoSposta { inizio: string; fine: string; emailInviata: boolean }
// Con un backend non aggiornato la risposta di annulla può non avere `data`.
interface EsitoAnnulla { data?: { emailInviata?: boolean } }

export default function GestionePage() {
  const { token = '' } = useParams();
  const base = `/api/pubblico/prenotazioni/gestione/${encodeURIComponent(token)}`;
  const inCorso = useRef(false);
  const [dati, setDati] = useState<DatiGestione | null>(null);
  const [nonTrovata, setNonTrovata] = useState(false);
  const [errore, setErrore] = useState('');
  const [esito, setEsito] = useState('');
  const [sposta, setSposta] = useState(false);
  const [inizio, setInizio] = useState<string | null>(null);
  const [lavoro, setLavoro] = useState(false);
  const [versioneSlot, setVersioneSlot] = useState(0);

  const carica = useCallback(() => {
    // @api GET /api/pubblico/prenotazioni/gestione/:token -> {"success":true,"data":"object"}
    pubblicoFetch<{ data: DatiGestione }>(base)
      .then(r => setDati(r.data))
      .catch(e => ((e as ErrorePubblico).status === 404 ? setNonTrovata(true) : setErrore((e as ErrorePubblico).message)));
  }, [base]);

  useEffect(() => { carica(); }, [carica]);

  useTitoloPagina(dati ? (dati.sede ? `Il tuo appuntamento — ${dati.sede.nome}` : 'Il tuo appuntamento') : null);

  // Scegliendo un nuovo orario l'errore precedente (es. "orario occupato") non vale più.
  const scegliOrario = (nuovoInizio: string) => {
    setInizio(nuovoInizio);
    setErrore('');
  };

  const confermaSpostamento = async () => {
    // Il ref blocca anche un secondo tocco che arrivasse prima del nuovo render.
    if (!inizio || inCorso.current) return;
    inCorso.current = true;
    setLavoro(true);
    setErrore('');
    try {
      // @api POST /api/pubblico/prenotazioni/gestione/:token/sposta {"inizio":"string"} -> {"success":true,"data":"object"}
      const r = await pubblicoFetch<{ data: EsitoSposta }>(`${base}/sposta`, { method: 'POST', body: JSON.stringify({ inizio }) });
      const nuovoOrario = formatQuandoRoma(r.data.inizio);
      setEsito(r.data.emailInviata
        ? `Appuntamento spostato a ${nuovoOrario}. Ti abbiamo inviato un'email di conferma.`
        : `Appuntamento spostato a ${nuovoOrario}. Annota il nuovo orario.`);
      setSposta(false);
      setInizio(null);
      carica();
    } catch (err) {
      const ep = err as ErrorePubblico;
      setErrore(ep.message);
      if (ep.codice === 'SLOT_OCCUPATO') {
        setInizio(null);
        setVersioneSlot(v => v + 1);
      }
    } finally {
      inCorso.current = false;
      setLavoro(false);
    }
  };

  const annulla = async () => {
    if (inCorso.current) return;
    if (!window.confirm("Vuoi davvero annullare l'appuntamento?")) return;
    inCorso.current = true;
    setLavoro(true);
    setErrore('');
    try {
      // @api POST /api/pubblico/prenotazioni/gestione/:token/annulla -> {"success":true,"data":{"emailInviata":"boolean"}}
      const r = await pubblicoFetch<EsitoAnnulla>(`${base}/annulla`, { method: 'POST', body: '{}' });
      // Si promette l'email solo se il backend dice che è partita; senza `data` testo neutro.
      setEsito(r?.data?.emailInviata
        ? "Appuntamento annullato. Ti abbiamo inviato un'email di conferma."
        : 'Appuntamento annullato.');
      carica();
    } catch (err) {
      setErrore((err as ErrorePubblico).message);
    } finally {
      inCorso.current = false;
      setLavoro(false);
    }
  };

  if (nonTrovata) return <PaginaNonDisponibile />;
  if (!dati) return <Contenitore><p className="bop-pubblico__testo" role={errore ? 'alert' : undefined}>{errore || 'Caricamento…'}</p></Contenitore>;

  const modificabile = dati.appuntamento.stato === 'PRENOTATO' && dati.stato === 'APERTA';
  const tema = dati.aspetto?.tema ?? null;
  return (
    <Contenitore tema={tema}>
      <Intestazione sede={dati.sede} tokenPagina={dati.tokenPagina} logoImpresa={tema?.logoImpresa} />
      <div className="bop-pubblico__card">
        <h1 className="bop-pubblico__titolo">Il tuo appuntamento</h1>
        <p className="bop-pubblico__testo">
          {maiuscolaIniziale(formatQuandoRoma(dati.appuntamento.inizio))}{dati.sede ? ` presso ${dati.sede.nome}` : ''}
          {dati.cantiere ? ` · ${dati.cantiere.nome}` : ''}
        </p>
        {/* Appena annullato basta il messaggio di successo: l'avviso rosso comparirebbe insieme al verde. */}
        {dati.appuntamento.stato === 'ANNULLATO' && !esito && <p className="bop-pubblico__errore" role="alert">Questo appuntamento è annullato.</p>}
        {esito && <p className="bop-pubblico__successo" aria-live="polite">{esito}</p>}
        {errore && <p className="bop-pubblico__errore" role="alert">{errore}</p>}
        {modificabile && !sposta && (
          <div className="bop-pubblico__azioni">
            <button type="button" className="bop-btn bop-btn-primary" onClick={() => setSposta(true)} disabled={lavoro}>Sposta</button>
            <button type="button" className="bop-btn bop-btn-danger" onClick={annulla} disabled={lavoro}>Annulla appuntamento</button>
          </div>
        )}
        <Contatti sede={dati.sede} />
      </div>
      {modificabile && sposta && (
        <>
          <SceltaSlot percorsoSlot={`${base}/slot`} valore={inizio} onScegli={scegliOrario} versione={versioneSlot} />
          <div className="bop-pubblico__azioni">
            <button type="button" className="bop-btn bop-btn-primary" onClick={confermaSpostamento} disabled={lavoro || !inizio}>Conferma nuovo orario</button>
            <button type="button" className="bop-btn bop-btn-secondary" onClick={() => { setSposta(false); setInizio(null); }}>Lascia com'è</button>
          </div>
        </>
      )}
    </Contenitore>
  );
}
